import type { LocationIntelligence } from './atlasApi';
import { number } from './atlasModel';

export function AreaPopulationDetails({ data }: { data: LocationIntelligence | undefined }) {
  return (
    <>
      <section>
        <h4>
          Population & household growth <span>County</span>
        </h4>
        {data?.growth?.metrics.map((m) => (
          <div className="atlas-growth-row" key={m.id}>
            <div>
              <strong>{m.label}</strong>
              <strong>
                {m.percent === null
                  ? 'Unavailable'
                  : (m.percent > 0 ? '+' : '') + m.percent.toFixed(1) + '%'}
              </strong>
            </div>
            <p>
              {number(m.previous)} → {number(m.current)} <span>Change: {number(m.change)}</span>
            </p>
            <small>
              {m.changeMargin === null
                ? 'Change uncertainty unavailable'
                : 'Change ± ' + number(m.changeMargin) + ' (90% margin)'}
              {m.distinguishable === false
                ? ' · Within sampling uncertainty'
                : m.distinguishable === true
                  ? ' · Exceeds sampling uncertainty'
                  : ''}
            </small>
          </div>
        ))}
        {!data?.growth ? (
          <p className="atlas-muted">Growth observations unavailable.</p>
        ) : (
          <p className="atlas-provenance">
            {data.growth.geography ?? 'County unavailable'}
            <br />
            {data.growth.previousPeriod} → {data.growth.currentPeriod}
            <br />
            {data.growth.basis}
          </p>
        )}
      </section>
      <section>
        <h4>
          Daytime population <span>County</span>
        </h4>
        <dl className="atlas-metrics">
          <div>
            <dt>Commuter-adjusted population</dt>
            <dd>{number(data?.daytime?.population ?? null)}</dd>
            <small>
              {data?.daytime?.margin != null
                ? '± ' + number(data.daytime.margin) + ' · approximate 90% margin'
                : 'Uncertainty unavailable'}
            </small>
          </div>
          <div>
            <dt>Workers at workplaces</dt>
            <dd>{number(data?.daytime?.workplaceWorkers ?? null)}</dd>
            <small>Workers, not job openings</small>
          </div>
        </dl>
        <dl className="atlas-facts">
          <div>
            <dt>Residents</dt>
            <dd>{number(data?.daytime?.residents ?? null)}</dd>
          </div>
          <div>
            <dt>Resident workers</dt>
            <dd>{number(data?.daytime?.residentWorkers ?? null)}</dd>
          </div>
          <div>
            <dt>Net commuters</dt>
            <dd>{number(data?.daytime?.netCommuters ?? null)}</dd>
          </div>
        </dl>
        <p className="atlas-provenance">
          {data?.daytime?.geography ?? 'County unavailable'}
          <br />
          {data?.daytime?.period}
          <br />
          {data?.daytime?.method ?? 'Commuter-adjusted population observations unavailable.'}
        </p>
      </section>
    </>
  );
}

export function ParcelDetails({ data }: { data: LocationIntelligence | undefined }) {
  return (
    <section>
      <h4>
        Ownership & zoning <span>Selected point</span>
      </h4>
      {!data?.parcels?.records.length ? (
        <p className="atlas-muted">No verified parcel observations returned.</p>
      ) : null}
      {data?.parcels?.truncated ? (
        <p className="atlas-notice">
          First 25 intersecting records shown. Additional records exist.
        </p>
      ) : null}
      {data?.parcels?.records.map((p, index) => (
        <article className="atlas-parcel" key={p.id + ':' + index}>
          <h5>{p.parcelNumber ?? 'Parcel identifier unavailable'}</h5>
          {p.address ? <p>{p.address}</p> : null}
          <dl className="atlas-facts">
            <div>
              <dt>Recorded owner(s)</dt>
              <dd>{p.owners.join('; ') || 'Unavailable'}</dd>
            </div>
            <div>
              <dt>County-reported acres</dt>
              <dd>{number(p.acres)}</dd>
            </div>
            <div>
              <dt>Recorded land use</dt>
              <dd>{p.landUse ?? 'Unavailable'}</dd>
            </div>
            <div>
              <dt>Zoning code</dt>
              <dd>{p.zoning.code ?? 'Unavailable'}</dd>
            </div>
            <div>
              <dt>Zoning description</dt>
              <dd>{p.zoning.description ?? 'Unavailable'}</dd>
            </div>
            <div>
              <dt>Standardized zoning</dt>
              <dd>
                {[p.zoning.type, p.zoning.subtype].filter(Boolean).join(' / ') || 'Unavailable'}
              </dd>
            </div>
            <div>
              <dt>Parcel source refresh</dt>
              <dd>{p.refreshedAt ?? 'Date unavailable'}</dd>
            </div>
            <div>
              <dt>Zoning data date</dt>
              <dd>{p.zoning.dataDate ?? 'Date unavailable'}</dd>
            </div>
          </dl>
          <div className="atlas-parcel-links">
            {p.sourceUrl ? (
              <a href={p.sourceUrl} target="_blank" rel="noreferrer">
                Parcel source ↗
              </a>
            ) : null}
            {p.zoning.ordinanceUrl ? (
              <a href={p.zoning.ordinanceUrl} target="_blank" rel="noreferrer">
                Zoning ordinance ↗
              </a>
            ) : null}
          </div>
        </article>
      ))}
      <p className="atlas-provenance">
        {data?.parcels?.coverage ?? 'Select a location to check parcel coverage.'}
      </p>
    </section>
  );
}
